/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React, { FC, useCallback, useState, useMemo, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Actionable,
  Text,
  SheetContainer,
  InputRadio,
  Bubble,
  ActionBar,
  useTheme,
  Divider,
  Title,
  Button,
  Stack,
  Icon,
  useViewport
} from "@bookingcom/bui-react";
import { SortIcon, FilterFunnelIcon, SparklesIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import { useI18n, Trans } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { SBSortingOrder } from "@bookingcom/flights-searchbox";
// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore, useActions } from "../../../store";
import { actions as searchActions } from "../../../store/search/actions";
import useCreateUrl from "../../../hooks/useCreateUrl";
import useSearchFilters from "../../../hooks/useSearchFilters";
import useModalDialog from "../../../hooks/useModalDialog";
import useGlobalContext from "../../../hooks/useGlobalContext";
import useEventTracking from "../../../hooks/useEventTracking";
import Frame from "../Frame";
import styles from "./SearchToolbar.module.css";
import SearchFiltersMobile from "../SearchFilters/SearchFilters.mobile";
import { useGetFiltersDraft } from "../SearchResults/components/boundsFilterHooks";
import flights_web_sr_gen_ai_summary_v3_mdot from "../../../utils/experiments/funnel/flights_web_sr_gen_ai_summary_v3_mdot";
import useUserAgent from "../../../hooks/useUserAgent";
import { trackWithDefaultStage } from "@flights/et-universal";
import flights_web_remove_fastest_sorting, {
  useTrackRemoveFastestSorting
} from "../../../utils/experiments/funnel/flights_web_remove_fastest_sorting";
import { useAppliedFilters } from "../../../hooks/search/useAppliedFilters";
import useLocaleContext from "../../../hooks/useLocaleContext";
import AiSummaryMobile from "../SearchResults/components/AiSummary.mobile";
import flights_web_budget_layover_airports_filters from "../../../utils/experiments/funnel/flights_web_budget_layover_airports_filters";

type RadioButtonChangeHandlerProps = {
  id: SBSortingOrder;
  checked: boolean;
};

const SearchToolbar: FC = () => {
  const { enhanceURLSearchParams } = useCreateUrl();
  const history = useHistory();
  const location = useLocation();
  const { applyFilters, applyFiltersAndSort } = useSearchFilters();
  const { isLarge } = useViewport();
  const { isMobile } = useUserAgent();
  const { locale } = useLocaleContext();

  const isResponsiveFixes = !isMobile && !isLarge && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);
  const isEligibleForAiSummary = locale?.code.toLowerCase().startsWith("en-");

  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const actions = useActions(searchActions);

  const removeFastestSorting = !!flights_web_remove_fastest_sorting.trackWithDefaultStage();

  // TODO: https://booking.atlassian.net/browse/FUNNEL-2949
  //  Move redirect to redirects.ts if flights_web_remove_fastest_sorting goes full-on
  useEffect(() => {
    if (removeFastestSorting) {
      const query = new URLSearchParams(location.search);
      const sort = query.get("sort");
      if (sort === "FASTEST") {
        query.set("sort", "BEST");
        window.location.href = `${location.pathname}?${query}`;
      }
    }
  }, [location.search, location.pathname, removeFastestSorting]);

  const initialSort = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return query.get("sort") || "BEST";
  }, [location.search]);

  const [sortSelected, setSortSelected] = useState<SBSortingOrder>(initialSort as SBSortingOrder);
  const [sortModified, setSortModified] = useState(false);

  const onSortRadioButtonChange = useCallback(
    ({ id }: RadioButtonChangeHandlerProps) => {
      setSortModified(id !== initialSort);
      setSortSelected(id);
      actions.setSortOrder(id);
    },
    [actions, initialSort]
  );

  const onSortModalClose = useCallback(() => {
    // execute the sorters only when there is change from initial value
    if (!sortModified) {
      return;
    }

    switch (sortSelected) {
      case "BEST":
        flights_web_remove_fastest_sorting.goals.selectedBestSorting();
        break;
      case "CHEAPEST":
        flights_web_remove_fastest_sorting.goals.selectedCheapestSorting();
        break;
      case "GENIUS_DEALS":
        flights_web_remove_fastest_sorting.goals.selectedGeniusSorting();
        break;
      case "FASTEST":
        flights_web_remove_fastest_sorting.goals.selectedFastestSorting();
    }

    const params = new URLSearchParams(location.search);
    params.set("sort", sortSelected);
    params.delete("page");

    enhanceURLSearchParams(params);
    history.replace({
      pathname: location.pathname,
      search: params.toString()
    });
  }, [enhanceURLSearchParams, history, location.pathname, location.search, sortModified, sortSelected]);

  const filtersDraftWithBounds = useGetFiltersDraft();
  const onFiltersModalClose = useCallback(() => {
    if (isEligibleForAiSummary && flights_web_sr_gen_ai_summary_v3_mdot.trackWithDefaultStage()) {
      applyFiltersAndSort(filtersDraftWithBounds, sortModified ? sortSelected : undefined);
    } else {
      applyFilters(filtersDraftWithBounds);
    }
  }, [applyFilters, applyFiltersAndSort, filtersDraftWithBounds, sortModified, sortSelected, isEligibleForAiSummary]);

  const isStuck = window.scrollY >= 72;

  flights_web_sr_gen_ai_summary_v3_mdot.useTrackStages(isEligibleForAiSummary);

  const sortItem = (
    <SortItem sortSelected={sortSelected} onChange={onSortRadioButtonChange} onClose={onSortModalClose} />
  );
  const filtersItem = (
    <FiltersItem sortSelected={sortSelected} onSortChange={onSortRadioButtonChange} onClose={onFiltersModalClose} />
  );
  const searchSummaryItem = <SearchSummaryItem />;

  return (
    <div className={`${styles.wrapper} ${isResponsiveFixes ? styles.wrapperRes : ""}`}>
      <Frame
        direction="row"
        justifyContent="space-around"
        className={`${styles.toolbar} ${isResponsiveFixes ? styles.toolbarRes : ""}`}
      >
        {isEligibleForAiSummary && flights_web_sr_gen_ai_summary_v3_mdot.trackWithDefaultStage() ? (
          <>
            {filtersItem}
            {searchSummaryItem}
          </>
        ) : (
          <>
            {sortItem}
            {filtersItem}
          </>
        )}
      </Frame>

      {isStuck && <Divider />}
    </div>
  );
};

const FiltersItem: FC<{
  sortSelected: SBSortingOrder; // flights_web_sr_gen_ai_summary_v3_mdot
  onSortChange?: (props: RadioButtonChangeHandlerProps) => void; // flights_web_sr_gen_ai_summary_v3_mdot
  onClose?: () => void;
}> = ({ sortSelected, onSortChange, onClose }) => {
  const i18n = useI18n();
  const store = useStore(); // eslint-disable-line import/no-deprecated
  const { requestId } = useGlobalContext();
  const trackV2 = useEventTracking("search_results", requestId);
  const { locale } = useLocaleContext();
  const isEligibleForAiSummary = locale?.code.toLowerCase().startsWith("en-");

  const {
    searchResults: {
      availableFilters: { filteredTotalCount = 0, totalCount = 0 }
    }
  } = store;

  const appliedFilters = useAppliedFilters();

  const [isActive, setIsActive] = useState(false);
  const Modal = useModalDialog();

  const handleClose = useCallback(() => {
    setIsActive(false);
    onClose?.();
  }, [onClose]);

  const addNewFilters = !!flights_web_budget_layover_airports_filters.trackWithDefaultStage();

  return (
    <>
      <ToolbarItem
        iconSVG={
          isEligibleForAiSummary && flights_web_sr_gen_ai_summary_v3_mdot.trackWithDefaultStage()
            ? SortIcon
            : FilterFunnelIcon
        }
        text={
          isEligibleForAiSummary && flights_web_sr_gen_ai_summary_v3_mdot.trackWithDefaultStage()
            ? i18n.trans(t("flights_sort_and_filter"))
            : i18n.trans(t("flights_filter_button"))
        }
        number={appliedFilters.length}
        onClick={() => {
          trackV2("click_filters");
          setIsActive(true);

          if (isEligibleForAiSummary && Boolean(flights_web_sr_gen_ai_summary_v3_mdot.trackWithDefaultStage())) {
            flights_web_sr_gen_ai_summary_v3_mdot.goals.sortAndFilterClick();
          }
        }}
        ariaLabel={i18n.trans(t("flights_filter_button"))}
      />

      <Modal
        active={isActive}
        onClose={handleClose}
        fullHeight
        {...(addNewFilters
          ? {
              closeAriaLabel: i18n.trans(t("a11y_flights_mdot_filters_close")),
              title: (
                <Frame className={styles.modalContent}>
                  <Text tagName="h2" variant="headline_1">
                    {i18n.trans(t("flights_filter_name"))}
                  </Text>
                </Frame>
              )
            }
          : { hideClose: true })}
        footer={
          <ActionBar
            fillEqually
            button={{ text: i18n.trans(t("flights_action_done")), onClick: handleClose, size: "large" }}
          >
            <Title
              title={i18n.trans(
                t("flights_num_search_filters_applied", {
                  num_exception: appliedFilters.length,
                  variables: { num_filters: String(appliedFilters.length) }
                })
              )}
              subtitle={
                appliedFilters.length > 0
                  ? i18n.trans(
                      t("flights_number_filtered_results", {
                        num_exception: totalCount,
                        variables: { num_results: String(filteredTotalCount), num_total_results: String(totalCount) }
                      })
                    )
                  : i18n.trans(
                      t("flights_number_total_results", {
                        num_exception: filteredTotalCount,
                        variables: { num_results: String(totalCount) }
                      })
                    )
              }
            />
          </ActionBar>
        }
        fill={true}
      >
        <div className={styles.modalContent}>
          {addNewFilters ? undefined : (
            <Frame mt={3} mb={3}>
              <Text tagName="h2" variant="headline_1">
                {isEligibleForAiSummary && flights_web_sr_gen_ai_summary_v3_mdot.trackWithDefaultStage()
                  ? i18n.trans(t("flights_sort_and_filter"))
                  : i18n.trans(t("flights_filter_name"))}
              </Text>
            </Frame>
          )}

          {Boolean(isEligibleForAiSummary && flights_web_sr_gen_ai_summary_v3_mdot.trackWithDefaultStage()) && (
            <Stack className={styles.sortInsideFilters}>
              <Stack.Item>
                <Text variant="emphasized_1">{i18n.trans(t("flights_sort_by_title"))}</Text>
              </Stack.Item>
              <Stack.Item>
                <SortRadioList sortSelected={sortSelected} onChange={onSortChange} />
              </Stack.Item>
            </Stack>
          )}

          <SearchFiltersMobile />
        </div>
      </Modal>
    </>
  );
};

const SortItem: FC<{
  sortSelected: SBSortingOrder;
  onChange?: (props: RadioButtonChangeHandlerProps) => void;
  onClose?: () => void;
}> = ({ sortSelected, onChange, onClose }) => {
  const i18n = useI18n();
  const [isActive, setIsActive] = useState(false);

  const handleClose = useCallback(() => {
    setIsActive(false);
    onClose?.();
  }, [onClose]);

  return (
    <>
      <ToolbarItem
        iconSVG={SortIcon}
        text={i18n.trans(t("flights_filter_sort"))}
        onClick={() => {
          setIsActive(true);
        }}
        ariaLabel={i18n.trans(t("flights_filter_sort"))}
      />

      <SheetContainer
        title={i18n.trans(t("flights_filter_sort"))}
        closeAriaLabel={i18n.trans(t("flights_a11y_sort_close"))}
        active={isActive}
        onCloseTrigger={handleClose}
        position="bottom"
        footer={
          <Stack>
            <Divider />
            <Stack className={styles.button}>
              <div data-testid="search_toolbar_sort_modal_done">
                <Button text={i18n.trans(t("flights_action_done"))} wide={true} size="large" onClick={handleClose} />
              </div>
            </Stack>
          </Stack>
        }
      >
        <SortRadioList sortSelected={sortSelected} onChange={onChange} />
      </SheetContainer>
    </>
  );
};

const SortRadioList: FC<{
  sortSelected: SBSortingOrder;
  onChange?: (props: RadioButtonChangeHandlerProps) => void;
}> = ({ sortSelected, onChange }) => {
  const i18n = useI18n();
  const theme = useTheme();

  useTrackRemoveFastestSorting();
  const removeFastestSorting = !!flights_web_remove_fastest_sorting.trackWithDefaultStage();

  return (
    <>
      <Frame mb={4}>
        <SortRadioButton
          label={i18n.trans(t("flights_search_sort_best"))}
          id="BEST"
          checked={sortSelected === "BEST"}
          onChange={onChange}
        >
          <Frame attributes={{ style: { paddingLeft: "29px", color: theme.colors.color_foreground_alt } }}>
            <Text variant="small_1">
              <Trans tag="flights_search_sort_best_tooltip" />
            </Text>
          </Frame>
        </SortRadioButton>
      </Frame>

      <Frame mb={6}>
        <SortRadioButton
          label={i18n.trans(t("flights_search_sort_cheapest"))}
          id="CHEAPEST"
          checked={sortSelected === "CHEAPEST"}
          onChange={onChange}
        />
      </Frame>

      <Frame mb={4}>
        <SortRadioButton
          label={i18n.trans(t("flights_sort_by_genius_title"))}
          id="GENIUS_DEALS"
          checked={sortSelected === "GENIUS_DEALS"}
          onChange={onChange}
        >
          <Frame attributes={{ style: { paddingLeft: "29px", color: theme.colors.color_foreground_alt } }}>
            <Text variant="small_1">
              <Trans tag="flights_sort_by_genius_tooltip" />
            </Text>
          </Frame>
        </SortRadioButton>
      </Frame>

      {removeFastestSorting ? undefined : (
        <Frame mb={3}>
          <SortRadioButton
            label={i18n.trans(t("flights_search_sort_fastest"))}
            id="FASTEST"
            checked={sortSelected === "FASTEST"}
            onChange={onChange}
          />
        </Frame>
      )}
    </>
  );
};

const SortRadioButton: FC<{
  label: string;
  id: SBSortingOrder;
  checked: boolean;
  onChange?: (props: RadioButtonChangeHandlerProps) => void;
  children?: React.ReactNode;
}> = ({ label, id, checked, onChange, children }) => {
  return (
    <div
      onClick={() => {
        onChange?.({ id, checked: true });
      }}
      role="radio"
      aria-checked={checked}
      tabIndex={0}
    >
      <InputRadio
        label={label}
        name={id}
        key={id}
        checked={checked}
        attributes={{ "data-testid": `search_toolbar_radio_${label}` }}
      />
      {children}
    </div>
  );
};

const SearchSummaryItem: FC = () => {
  const i18n = useI18n();
  const [isActive, setIsActive] = useState(false);

  return (
    <>
      <ToolbarItem
        iconSVG={SparklesIcon}
        text={i18n.trans(t("flights_mdot_genai_summary_heading"))}
        onClick={() => {
          setIsActive(true);
          flights_web_sr_gen_ai_summary_v3_mdot.goals.summaryClick();
        }}
      />

      <AiSummaryMobile
        visible={isActive}
        onClose={() => {
          setIsActive(false);
        }}
      />
    </>
  );
};

const ToolbarItem: FC<{
  iconSVG: FC;
  text: string;
  number?: number;
  onClick: () => void;
  ariaLabel?: string;
}> = ({ iconSVG, text, number, onClick, ariaLabel }) => {
  return (
    <Actionable
      onClick={onClick}
      attributes={{
        "aria-label": ariaLabel,
        style: {
          width: "100%",
          justifyContent: "center",
          alignItems: "center"
        },
        "data-testid": `search_toolbar_${ariaLabel}`
      }}
    >
      <Stack direction="row" alignItems="center">
        <Icon svg={iconSVG} size="small" />
        <Text variant="body_2">{text}</Text>
        {number && number > 0 ? <Bubble text={number} ariaLabel={`${number} applied filters`} /> : null}
      </Stack>
    </Actionable>
  );
};

export default SearchToolbar;
